import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {Form} from '@ui/forms/form';
import {Fragment, useEffect, useRef, useState, useMemo} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {CheckCircleOutlineIcon} from '@ui/icons/material/CheckCircleOutline';
import {ArrowLeftIcon} from '@ui/icons/material/ArrowLeftIcon';
import {ArrowRightIcon} from '@ui/icons/material/ArrowRightIcon';
import {BillingCycleRadio} from '@common/billing/pricing-table/billing-cycle-radio';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {UpsellBillingCycle} from '@common/billing/pricing-table/find-best-price';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {FacebookIcon} from '@ui/icons/social/facebook';
import {InstagramIcon} from '@ui/icons/social/instagram';
import {YoutubeIcon} from '@ui/icons/social/youtube';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from '@common/contact/use-submit-contact-form';
import {useForm} from 'react-hook-form';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {useRecaptcha} from '@common/recaptcha/use-recaptcha';
import {LandingNavbar} from '../landing/landing-navbar';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {Accordion, AccordionItem} from '@ui/accordion/accordion';
import {
  ResellerPagePayload,
  useSubmitResellerForm,
} from '@common/reseller/use-submit-reseller-form';
import {useAdminSettings} from '@common/admin/settings/requests/use-admin-settings';
import {useAuth} from '@common/auth/use-auth';
// import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt  } from "react-icons/fa";
import {motion} from 'framer-motion';
import {
  Shield,
  Lock,
  Database,
  FileCheck,
  Users,
  Eye,
  ShieldCheck,
  Gauge,
  UserCog,
  RefreshCw,
  BarChart3,
  UserPlus,
  Upload,
  Globe2,
} from 'lucide-react';
import {ArrowRight, Sparkles} from 'lucide-react';
import '../landing/landing-page.css';
import {useThemeSelector} from '@ui/themes/theme-selector-context';
import { Helmet } from '@common/seo/helmet';

export function SeoSpecialist() {
  const query = useProducts('pricingPage');
  const [selectedCycle, setSelectedCycle] =
    useState<UpsellBillingCycle>('month');

  const {selectThemeTemporarily} = useThemeSelector();
  useEffect(() => {
    selectThemeTemporarily('light');
    return () => selectThemeTemporarily(null);
  }, [selectThemeTemporarily]);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
  }, []);

  const services = [
  {
    title: 'SEO Report Storage',
    description:
       (
    <>
      SEO specialists create reports, spreadsheets, and analytics files daily. {" "}
      <a
        href="/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        SpaceByte Cloud storage  
      </a>{" "}
       keeps these documents protected and easy to access whenever needed.
    </>
  ),
    icon: BarChart3, 
  },
  {
    title: 'Remote File Access',
    description:
      'SEO work often happens in different places. Cloud storage lets specialists open reports and project files from any device anytime.',
    icon: Globe2,
  },
  {
    title: 'Campaign Organization',
    description:
      'Managing multiple SEO campaigns can get confusing. Creating folders for each client helps keep research, reports, and documents properly organized.',
    icon: Database,
  },
  {
    title: 'Easy File Sharing',
    description:
      'SEO specialists frequently send reports and updates. Cloud storage makes sharing project files with clients or teammates quick and simple.',
    icon: Upload,
  },
];

   type ColorKey = "sky" | "violet" | "emerald" | "amber";
    
    const useCases: {
      icon: React.ElementType;
      title: string;
      desc: React.ReactNode;
      color: ColorKey;
    }[] = [
      {
        icon: Eye,
        title: "SEO Consultants",
        desc: " Many consultants manage several client websites at once. Cloud storage helps keep keyword research, audit reports, and strategy files organized.",
        color: "sky",
      },
      {
        icon: BarChart3,
        title: "Digital Marketing Agencies",
        desc:  (
    <>
      Managing several SEO campaigns often creates many reports and files within {" "}
      <a
        href="/cloud-storage-for-digital-marketing"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        digital marketing 
      </a>{" "}
       agencies. Cloud storage keeps analytics data organized.
    </>
  ),
        color: "violet",
      },
      {
        icon: Gauge,
        title: "Technical SEO Experts",
        desc: " Daily SEO work involves crawl reports, site performance data, and technical audits. Cloud storage keeps these files secure and accessible.",
        color: "emerald",
      },
      {
        icon: UserCog,
        title: "Freelance SEO Specialists",
          desc: "Working with different clients means handling many reports and project files. Cloud storage keeps everything organized from different locations.",
        color: "amber",
      },
    ];
    
    const colorMap: Record<
      ColorKey,
      {
        border: string;
        shadow: string;
        iconBg: string;
        iconBorder: string;
        iconColor: string;
        accent: string;
      }
    > = {
      sky: {
        border: "rgba(14,165,233,0.30)",
        shadow: "rgba(14,165,233,0.20)",
        iconBg: "rgba(14,165,233,0.09)",
        iconBorder: "rgba(14,165,233,0.20)",
        iconColor: "#0284c7",
        accent: "#38bdf8",
      },
      violet: {
        border: "rgba(124,58,237,0.30)",
        shadow: "rgba(124,58,237,0.20)",
        iconBg: "rgba(124,58,237,0.09)",
        iconBorder: "rgba(124,58,237,0.20)",
        iconColor: "#6d28d9",
        accent: "#818cf8",
      },
      emerald: {
        border: "rgba(5,150,105,0.30)",
        shadow: "rgba(5,150,105,0.20)",
        iconBg: "rgba(5,150,105,0.09)",
        iconBorder: "rgba(5,150,105,0.20)",
        iconColor: "#047857",
        accent: "#34d399",
      },
      amber: {
        border: "rgba(217,119,6,0.30)",
        shadow: "rgba(217,119,6,0.20)",
        iconBg: "rgba(217,119,6,0.09)",
        iconBorder: "rgba(217,119,6,0.20)",
        iconColor: "#b45309",
        accent: "#fbbf24",
      },
    };
  

  const steps = [
    {
      icon: UserPlus,
      number: '01',
      title: 'Sign Up',
      description:
        'Create your account in seconds with no credit card required',
    },
    {
      icon: Upload,
      number: '02',
      title: 'Upload Files',
      description:
        'Upload files or sync folders instantly with secure, high-speed cloud storage',
    },
    {
      icon: Globe2,
      number: '03',
      title: 'Access Anywhere',
      description: 'Access your data from any device, anywhere in the world',
    },
  ];

  const isMobile = window.innerWidth <= 640;
  const styles: Record<string, React.CSSProperties> = {
    laptopContainer: {
      position: 'relative',
      width: '100%',
      maxWidth: '520px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

    laptopScreen: {
      width: '100%',
      aspectRatio: '16 / 10',
      background: '#fff',
      border: 'clamp(6px, 1.5vw, 10px) solid #1E293B',
      borderRadius: '14px 14px 0 0',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },

    webcam: {
      width: '5px',
      height: '5px',
      background: '#334155',
      borderRadius: '50%',
      position: 'absolute',
      top: '3px',
      left: '50%',
      transform: 'translateX(-50%)',
    },

    displayArea: {
      padding: '18px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },

    dashboardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #F1F5F9',
      marginBottom: isMobile ? '6px' : '18px',
      paddingBottom: isMobile ? '4px' : '10px',
    },

    navLinks: {
      display: 'flex',
      gap: '12px',
      fontSize: '9px',
      fontWeight: '600',
      color: '#94A3B8',
      flexWrap: 'wrap',
    },

    activeLink: {color: '#2563EB', borderBottom: '1px solid #2563EB'},

    mainTitle: {
      fontSize: 'clamp(13px, 2vw, 16px)',
      fontWeight: '700',
      color: '#1E293B',
      margin: 0,
    },

    subTitle: {
      fontSize: 'clamp(9px, 1.6vw, 10px)',
      color: '#64748B',
      marginBottom: 'clamp(8px, 2vw, 18px)',
    },

    chartContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      flex: 1,
      paddingBottom: '10px',
      gap: '10px',
    },

    column: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '18%',
      minWidth: '50px',
    },

    barWrap: {
      width: '100%',
      height: 'clamp(90px, 18vw, 130px)',
      background: '#F8FAF8',
      display: 'flex',
      alignItems: 'flex-end',
      borderRadius: '6px',
      marginBottom: 'clamp(6px, 2vw, 12px)',
      overflow: 'hidden',
    },

    bar: {
      width: '100%',
      borderRadius: '4px 4px 2px 2px',
    },

    priceLabel: {
      fontSize: 'clamp(9px, 1.4vw, 10px)',
      fontWeight: '700',
      color: '#1E293B',
    },

    nameLabel: {
      fontSize: 'clamp(8px, 1.2vw, 9px)',
      color: '#94A3B8',
      marginTop: '2px',
      textAlign: 'center',
    },

    laptopBase: {
      width: '108%',
      height: 'clamp(6px, 1.5vw, 10px)',
      background: '#475569',
      borderRadius: '0 0 20px 20px',
      position: 'relative',
    },

    baseNotch: {
      width: '60px',
      height: '4px',
      background: '#1E293B',
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      borderRadius: '0 0 6px 6px',
    },
  };

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [paused, setPaused] = useState(false);

  const logos = [
    {src: '/images/LandingImg/theweek.png', name: 'theweek'},
    {src: '/images/LandingImg/pti.jpeg', name: 'pti'},
    {src: '/images/LandingImg/loktejenglish.png', name: 'loktejenglish'},
    {src: '/images/LandingImg/samachar.png', name: 'samachar'},
    {src: '/images/LandingImg/up18new.webp', name: 'up18new'},
    {src: '/images/LandingImg/wow.png', name: 'wow'},
    {src: '/images/LandingImg/lusakatimes.webp', name: 'lusakatimes'},
  ];

  // duplicate for infinite scroll
  const items = [...logos, ...logos];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let raf = 0;
    let x = 0;

    const speed = 0.65;

    const animate = () => {
      if (!paused) {
        x -= speed;

        const halfWidth = track.scrollWidth / 2;
        if (Math.abs(x) >= halfWidth) x = 0;

        track.style.transform = `translateX(${x}px)`;
      }

      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      question: 'Why do SEO specialists need cloud storage?',
      answer:
        'SEO specialists handle many files like keyword research, audit reports, and analytics data. Cloud storage helps keep these files organized and easy to access from different devices.',
    },
    {
      question: 'How does SpaceByte help SEO professionals manage their files?',
      answer:
        'SpaceByte gives SEO professionals one place to keep project files, reports, and campaign documents. It also makes sharing updates with clients or team members much easier.',
    },
    {
      question: ' Can cloud storage help manage multiple SEO client projects?',
      answer:
        'Yes. When working with many clients, cloud storage makes it easier to keep files sorted. You can create separate folders for each project or campaign.',
    },
    {
      question:
        'Can SEO specialists share reports with clients using cloud storage?',
      answer:
        'Yes. Cloud storage makes it simple to share reports, campaign files, and documents with clients or team members through secure file links. ',
    },
  ];

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const reviews = useMemo(
    () => [
      {
        name: 'Rohan Shah',
        role: 'SEO Specialist',
        review:
          'As an SEO specialist, I manage keyword research, reports, and analytics files every day. Keeping everything stored in one place makes it easier to track projects and share updates with clients.',
        avatar: '',
        rating: 5,
        accent: '#2563EB',
        light: '#EAF2FF',
      },
      {
        name: 'Neha Patel',
        role: 'Digital Marketing Manager',
        review:
          'Handling multiple SEO campaigns means working with many reports and documents. With SpaceByte cloud storage, I can keep project files organized and access them whenever needed.',
        avatar: '',
        rating: 4.2,
        accent: '#7C3AED',
        light: '#F3E8FF',
      },

      {
        name: 'Amit Verma',
        role: 'Freelance SEO Consultant',
        review:
          'Working with different SEO clients requires managing a lot of research and audit files. Having them stored in one place makes my workflow much smoother.',
        avatar: '',
        rating: 4,
        accent: '#0284C7',
        light: '#E0F2FE',
      },
    ],
    [],
  );

  const total = reviews.length;

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      {threshold: 0.12},
    );

    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const prev = () => setActiveIndex(p => (p === 0 ? total - 1 : p - 1));
  const next = () => setActiveIndex(p => (p === total - 1 ? 0 : p + 1));

  const cardWidth = 450; // desktop reference
  const gap = 24;

  // Offset only for desktop logic
  const offset = activeIndex * (cardWidth + gap);

  // const TawkTo = () => {
  //   useEffect(() => {
  //     const Tawk_API = (window as any).Tawk_API || {};
  //     const Tawk_LoadStart = new Date();

  //     const script = document.createElement('script');
  //     script.async = true;
  //     script.src = 'https://embed.tawk.to/679a04db3a8427326076ac96/1iioq5v0g';
  //     script.charset = 'UTF-8';
  //     script.setAttribute('crossorigin', '*');

  //     document.body.appendChild(script);

  //     return () => {
  //       document.body.removeChild(script);
  //     };
  //   }, []);

  //   return null; // This component doesn't render anything
  // };

  return (
    <Fragment>


       <Helmet>
              <title>Best Cloud Storage for SEO Specialists</title>
              <meta
                name="description"
                content="Cloud storage for SEO specialists to securely store keyword research, reports, analytics, and client files while keeping campaigns organized and easily accessible."
              />
              <meta name="keywords" content="Cloud Storage for SEO Specialists" />
              <link rel="canonical" href="https://spacebyte.in/cloud-storage-for-seo-specialists" />
            </Helmet>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
        menuPosition="homepage-navbar"
      />

      {/* =============== HERO SECTION  =============== */}
      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row">
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Modern Cloud Storage for {" "} 
              <br />
              <span className="color-primary-gradient">SEO Specialists</span>
              <br />
              {/* <span className="color-primary-gradient"> SpaceByte</span> */}

            </h1>

            {/* Sub heading */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              SEO specialists manage research files, analytics data, and campaign reports daily. Cloud storage helps store SEO projects, audits, and client documents while keeping everything organized and accessible.

            </p>

          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-28 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-36 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg"
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-30 md:my-50 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              Start Your Cloud Storage in <span className="color-primary-gradient">Three Steps</span>{" "}

            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing and sharing your files securely in minutes
            </p>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-90 left-0 right-0 h-2 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-30"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10  w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Animated Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className=" hidden md:block absolute top-[260px] left-[25%] right-[25%] h-3 bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] rounded-full origin-left"
          ></motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 text-black lg:py-50">
        <div className="landing-container z-10 flex flex-col items-center justify-between gap-8 px-4 md:px-8 lg:flex-row">
          {/* Left Section */}
          <div className="relative w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-[520px]">

              {/* Background Offset Block */}
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />

              {/* Image */}
              <img
                src="/images/seo-specialists/seo-specialists.webp"
                alt="cloud storage for business-storage "
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full space-y-16 lg:w-1/2 pt-14 md:pt-0">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              SEO Data Management <span className="color-primary-gradient">  Challenges   </span> 
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              SEO professionals handle many files, including keyword research, competitor analysis, technical audits, and campaign data. Managing these across multiple clients can be difficult. Cloud storage helps SEO specialists organize files, protect important reports, and keep data accessible for easy sharing anytime.
            </p>
          </div>
        </div>
      </section>

      <section
        id="Pricing"
        className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50"
      >
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Affordable <span className="color-primary-gradient"> Cloud Storage Pricing </span> Plans
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get affordable pricing with no hidden fees, encrypted cloud storage,
              and fast, seamless access to your files anytime, anywhere.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Section */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage:
                "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            {/* Premium Tag */}
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            {/* Icon and Text */}
            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">
                  5 TB or More?
                </h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB{' '}
                  <span className="text-lg font-normal text-black">
                    /Monthly
                  </span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with
                  End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or
                  Reach out to us
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">
                {CalculateDialogTrigger()}
              </div>

              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f5f8ff] py-30 md:py-50 relative overflow-hidden">
        <div className="landing-container z-10">
          {/* Header */}
        <div className="text-center space-y-8">
        <h3 className="font-archivo text-3xl font-bold md:text-4xl lg:text-5xl text-[#1f2937]">
            A Smarter Storage Solution for { " " } <br /><span className="color-primary-gradient"> SEO Professionals </span>
        </h3>
        <p className="font-inter text-sm md:text-base color-gray mt-10">
            Photographers manage many files after every shoot. SpaceByte cloud storage keeps RAW photos, edited images, and client galleries organized, secure, and easy to access or share.
        </p>
        </div>

          {/* Cards */}
          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 lg:grid-cols-4 ">
            {services.map((item, index) => (
              <div
                key={index}
                className="relative mb-16 rounded-2xl bg-white mt-12 md:mt-26 p-16 text-center shadow-md transition-all duration-300 hover:shadow-xl"
              >
                {/* Icon wrapper */}
                <div className="-mt-24 mb-16 flex justify-center">
                  <div
                    className="flex h-40 w-40 rotate-45 items-center justify-center rounded-xl bg-gradient-to-br
        from-[#3B82F6] to-[#06B6D4] shadow-lg sm:h-50 sm:w-50"
                  >
                    <item.icon className="h-16 w-16 -rotate-45 text-white sm:h-20 sm:w-20" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="mb-10 text-lg font-semibold text-[#111827]">
                  {item.title}
                </h3>

                {/* Line */}
                <div className="mx-auto mb-10 h-[3px] w-50 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#06B6D4]"></div>

                {/* Description */}
                <p className="text-sm leading-relaxed text-[#6B7280] sm:text-base">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section  className="relative overflow-hidden py-20 md:py-50 bg-white"
        style={{
          background: "#f8fafc",
        }}
      >
        <div className="landing-container z-10">
          {/* Header */}
          <div className="text-center mb-14 font-archivo">
                <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
                Why SpaceByte is the Best Choice
                <br /> 
                <span className="color-primary-gradient font-bold">for Digital Marketers</span>
                </h2>
                <p className="color-gray font-inter animate-fade-down text-base py-10 md:py-14">
                    SpaceByte offers content creators secure, fast, and scalable cloud storage. Easily store, manage, and share large videos, high-resolution images, and audio files while collaborating seamlessly and focusing on your creative workflow.
                </p> 
            </div>

          {/* Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px,1fr))",
              gap: 18,
            }}
          >
            {useCases.map((uc) => {
              const c = colorMap[uc.color];
              return (
                <div
                  key={uc.title}
                  style={{
                    background: "#fff",
                    border: `1px solid #e2e8f0`,
                    borderRadius: 20,
                    padding: "30px 26px",
                    transition: "all .3s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = c.border;
                    e.currentTarget.style.boxShadow = `0 18px 48px -8px ${c.shadow}`;
                    e.currentTarget.style.transform = "translateY(-5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow =
                      "0 2px 6px rgba(0,0,0,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 13,
                      background: c.iconBg,
                      border: `1px solid ${c.iconBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 18,
                    }}
                  >
                    <uc.icon size={22} color={c.iconColor} />
                  </div>

                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginBottom: 8,
                      color: "#0f172a",
                    }}
                  >
                    {uc.title}
                  </h3>

                  <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6 }}>
                    {uc.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">

          {/* Video Background */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Gradient Light */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold">
              Your Data, Your Control, Your Cloud
            </h2>

            <p className="text-white/80 text-base md:text-lg mt-20">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.

            </p>

            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      <section
        id="faq"
        className="bg-[#f5f8ff] relative overflow-hidden py-24 text-black lg:py-32"
      >
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files
                  anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div
                            className="illus-bar-fill"
                            style={{width: '73%'}}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="right-panel">
                {/* <p className="section-tag">Help Center</p> */}

                <h2 className="section-title">Quick Reference Guide</h2>

                <p className="section-desc">
                  Find answers to the most common questions about SpaceByte
                  Cloud Storage.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div
                      key={index}
                      className={`acc-item ${openIndex === index ? 'open' : ''}`}
                    >
                      <button
                        className="acc-header"
                        onClick={() => toggle(index)}
                      >
                        <div className="acc-num">
                          {String(index + 1).padStart(2, '0')}
                        </div>

                        <span className="acc-label">{item.question}</span>

                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div
                          className="acc-body-inner"
                          dangerouslySetInnerHTML={{__html: item.answer}}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={ref}
        className="relative overflow-hidden bg-white py-30 md:py-50"
      >
        {/* Background Orbs (same as ::before/::after) */}
        <div className="pointer-events-none absolute -left-[180px] -top-[180px] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,#dbeafe40,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[160px] -right-[160px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,#ede9fe40,transparent_70%)]" />

        <div className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-2 items-stretch gap-[56px] px-[clamp(20px,5vw,72px)] max-[900px]:grid-cols-1 max-[900px]:gap-[40px]">
          {/* ───────────────── LEFT ───────────────── */}
          <div
            className={[
              'transition-all duration-700 ease-out',
              visible
                ? 'translate-x-0 opacity-100'
                : '-translate-x-7 opacity-0',
            ].join(' ')}
          >
            {/* Heading */}
            <h2 className="font-archivo my-10 pt-6 text-[clamp(26px,3.2vw,46px)] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#0f172a] md:pt-30">
              What Our{' '}
              <span className="font-archivo color-primary-gradient bg-gradient-to-br">
                Customers Say
              </span>{' '}
            </h2>

            {/* Text */}
            <p className="font-archivo  mb-10 max-w-[440px] text-[clamp(14px,1.1vw,16px)] leading-[1.82] text-[#64748b]">
              See how SpaceByte delivers secure file storage, high-speed
              uploads, and advanced privacy protection. Read verified reviews
              before making your cloud storage decision.
            </p>

            {/* Controls */}
            <div className="my-20 flex flex-wrap  items-center gap-3">
              <button
                onClick={prev}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowLeftIcon />
              </button>

              <button
                onClick={next}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowRightIcon />
              </button>
            </div>

            {/* Dots */}
            <div className="my-16 ml-[6px] flex gap-[6px]">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={[
                    'h-[8px] rounded-[4px] transition-all duration-300',
                    i === activeIndex
                      ? 'w-[26px] bg-[#2563eb]'
                      : 'w-[8px] bg-[#cbd5e1]',
                  ].join(' ')}
                />
              ))}
            </div>

            {/* Progress */}
            <div className="mt-5 h-[3px] max-w-[220px] overflow-hidden rounded bg-[#e2e8f0]">
              <div
                className="h-full rounded bg-gradient-to-r from-[#2563eb] to-[#7c3aed] transition-all duration-500"
                style={{width: `${((activeIndex + 1) / total) * 100}%`}}
              />
            </div>
          </div>

          {/* ───────────────── RIGHT (SLIDER) ───────────────── */}
          <div
            className={[
              'w-full transition-all delay-200 duration-700 ease-out',
              visible ? 'translate-x-0 opacity-100' : 'translate-x-7 opacity-0',
            ].join(' ')}
          >
            <div className="relative w-full overflow-hidden">
              {/* right fade */}
              <div className="pointer-events-none absolute right-0 top-0 z-[2] h-full w-[64px] bg-gradient-to-l from-white to-transparent" />

              <div
                className="flex items-stretch gap-6 transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                style={{
                  transform: `translateX(-${offset}px)`,
                }}
              >
                {reviews.map((item, i) => (
                  <ReviewCard
                    key={i}
                    item={item}
                    isActive={i === activeIndex}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] py-20 md:py-32">
        {/* Animated Background */}
        <div className="landing-container absolute inset-0 z-10">
          <div className="absolute left-0 top-0 h-96 w-96 animate-pulse rounded-full bg-white/10 blur-3xl"></div>
          <div
            className="absolute bottom-0 right-0 h-96 w-96 animate-pulse rounded-full bg-white/10 blur-3xl"
            style={{animationDelay: '1s'}}
          ></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="cta-grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="white"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-40 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{scale: 0}}
              whileInView={{scale: 1}}
              viewport={{once: true}}
              transition={{type: 'spring', delay: 0.2}}
              className="mb-12 inline-flex h-60 w-60 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm"
            >
              <Sparkles className="h-30 w-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: 0.3}}
              className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl xl:text-6xl"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: 0.4}}
              className="mx-auto my-16 max-w-2xl text-lg text-white/90 sm:text-xl"
            >
              Join thousands of businesses trusting secure cloud storage to
              protect critical data.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: 0.5}}
              className="flex flex-col items-center justify-center gap-10 sm:flex-row"
            >
              <a
                href="/register"
                className="hover:bg-gray-100 btn group flex transform items-center gap-2 rounded-xl bg-white px-16 py-14 font-semibold text-[#1E3A8A] transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-white/20"
              >
                Get 25 GB FREE - No Card Needed
              </a>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{opacity: 0}}
              whileInView={{opacity: 1}}
              viewport={{once: true}}
              transition={{delay: 0.6}}
              className="mt-20 flex flex-wrap items-center justify-center gap-8 text-sm text-white/80"
            ></motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] pb-8 pt-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-6">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="padding-logo padding-top mr-12 space-y-4">
                <a href="https://spacebyte.in/" className="flex-shrink-0">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="pb-10 pt-8 font-medium">
                    SPACEBYTE CLOUD STORAGE
                  </p>
                  <p className="flex items-center gap-2 pb-10 pt-3">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar,
                    Ahmedabad, Gujarat, India - 382345
                  </p>
                </div>
                <div>
                  <p className="pb-10 pt-3 text-base font-bold">
                    FOLLOW US FOR "SPACEBYTE.IN"
                  </p>
                  <div className="logg mb-10 mt-3 flex gap-4">
                    <ul className="flex space-x-16 pr-10 pt-3">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho ">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className="px-30 py-8 md:py-20">
              <h2 className="mb-10 text-start text-xl  font-semibold ">
                Product
              </h2>
              <ul className="text-gray-400 space-y-3 text-start text-sm">
                <li>
                  <a
                    href="#features"
                    className="text-start transition-colors hover:text-white"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#Pricing"
                    className="transition-colors hover:text-white"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#security"
                    className="transition-colors hover:text-white"
                  >
                    Security
                  </a>
                </li>
                <li></li>
              </ul>
            </div>

            {/* Company Column */}
            <div className="px-30 py-8 md:py-20">
              <h2 className="mb-10 text-start text-xl  font-semibold">
                Company
              </h2>
              <ul className="text-gray-400 space-y-3 text-start text-sm">
                <li>
                  <a
                    href="/about-us"
                    className="transition-colors hover:text-white"
                  >
                    About Us
                  </a>
                </li>
                {/* <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li> */}
                <li>
                  <a
                    href="/contact"
                    className="transition-colors hover:text-white"
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className="px-30 py-8 md:py-20">
              <h2 className="mb-10 text-start text-xl  font-semibold">
                Resources
              </h2>
              <ul className="text-gray-400 space-y-3 text-start text-sm">
                <li>
                  <a
                    href="/pages/blog"
                    className="transition-colors hover:text-white"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#faq" className="transition-colors hover:text-white">
                    FAQs
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="transition-colors hover:text-white"
                  >
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
              </ul>
            </div>

            {/* Legal Column */}
            <div className="px-30 py-8 md:py-20">
              <h3 className="mb-10 text-start text-xl  font-semibold">Legal</h3>
              <ul className="text-gray-400 space-y-3 text-start text-sm">
                <li>
                  <a href="#" className="transition-colors hover:text-white">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="transition-colors hover:text-white">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter mt-5 border-t border-t-primary/80 py-16 text-center">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}

function CalculateDialogTrigger() {
  const form = useForm<ContactPagePayload>({
    defaultValues: {
      message: '', // Initialize message field
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const submitForm = useSubmitContactForm(form);
  const {verify, isVerifying} = useRecaptcha('contact');

  return (
    <>
      <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
        <button
          className="btn w-full rounded-lg !bg-gradient-to-r !from-[#0072ff]
                !to-[#00c6ff] font-semibold !text-white  shadow-lg
                transition hover:shadow-xl"
        >
          <span>Get Quote</span>
        </button>
        <Dialog className="w-full max-w-lg rounded-xl p-6">
          <DialogHeader
            className="font-archivo text-2xl font-semibold"
            showDivider
          >
            <Trans message="Storage Cost Calculator" />
          </DialogHeader>
          <DialogBody>
            <div className="font-inter flex items-center justify-center">
              Enter the storage size (whole number greater than **5 TB**) to
              calculate your monthly cost.
            </div>
            <Form
              form={form}
              className="mt-4 flex flex-col items-center text-center"
              onSubmit={async value => {
                calculateCost(value);
              }}
            >
              <FormTextField
                label={<Trans message="Enter Storage in TB" />}
                name="tb"
                required
                type="number"
                className="mb-6 w-full rounded-lg p-2"
              />
              <div className="text-red-500 text-md" id="errorMsg"></div>
              <div className="text-blue-500 text-md" id="resultOutput"></div>
              <div className="mt-6 flex w-full justify-between gap-4">
                <button className="btn-warning mt-16" type="submit">
                  Calculate
                </button>
                <Button
                  color="positive"
                  variant="outline"
                  className="mt-16 rounded-lg"
                  type="button"
                  onClick={() => {
                    const resultOutput =
                      document.getElementById('resultOutput')?.textContent;
                    form.setValue('message', resultOutput || ''); // Set the value of message field
                    setIsOpen(false); // Close current dialog
                    setIsContactDialogOpen(true); // Open Contact Us dialog
                  }}
                >
                  Contact Us
                </Button>
              </div>
            </Form>
          </DialogBody>
        </Dialog>
      </DialogTrigger>

      {isContactDialogOpen && (
        <DialogTrigger
          type="modal"
          isOpen={isContactDialogOpen}
          onOpenChange={setIsContactDialogOpen}
        >
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader
              className="font-archivo text-2xl font-semibold"
              showDivider
            >
              <Trans message="Contact Us" />
            </DialogHeader>
            <DialogBody>
              {submitForm.isPending ? (
                <FullPageLoader />
              ) : (
                <Form
                  form={form}
                  className="flex flex-col gap-4"
                  onSubmit={async value => {
                    const isValid = await verify();
                    if (isValid) {
                      submitForm.mutate(value, {
                        onSuccess: () => {
                          setIsContactDialogOpen(false); // Close the dialog on success
                        },
                      });
                    }
                  }}
                >
                  <FormTextField
                    label={<Trans message="Name" />}
                    name="name"
                    required
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Email" />}
                    name="email"
                    required
                    type="email"
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Mobile Number" />}
                    name="mobileNumber"
                    required
                    type="number"
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Message" />}
                    name="message"
                    required
                    inputElementType="textarea"
                    className="rounded-lg p-2"
                    rows={5}
                  />

                  <div className="mt-4 flex w-full justify-between gap-4">
                    <Button
                      variant="flat"
                      onClick={() => setIsContactDialogOpen(false)}
                      className="rounded-lg px-4 py-2 text-lg"
                    >
                      <Trans message="Back" />
                    </Button>
                    <Button
                      type="submit"
                      variant="flat"
                      color="primary"
                      className="rounded-lg px-4 py-2 text-lg"
                    >
                      <Trans message="Send Message" />
                    </Button>
                  </div>
                </Form>
              )}
            </DialogBody>
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}

function calculateCost({tb}: any) {
  const tbInput: any = tb;
  const errorMsg: any = document.getElementById('errorMsg');
  const resultOutput: any = document.getElementById('resultOutput');

  // Reset messages
  errorMsg.textContent = '';
  resultOutput.textContent = '';

  // Validation: Ensure input is a positive integer and greater than 5
  if (!/^\d+$/.test(tbInput)) {
    errorMsg.textContent = 'Error: Storage must be a whole number!';
    return;
  }

  const tbValue = parseInt(tbInput, 10);

  if (tbValue <= 5) {
    errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
    return;
  }

  // Conversion and cost calculation
  const gbStorage = tbValue * 1024; // Convert TB to GB
  const cost = gbStorage * 0.15; // Calculate cost

  // Display result
  resultOutput.innerHTML = `
      Storage: <strong>${tbValue.toLocaleString()} TB</strong><br>
      Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
      GST and Convenience Fee Extra**
  `;
}

function ReviewCard({item, isActive}: {item: any; isActive: boolean}) {
  const {name, role, avatar, review, accent, light, rating} = item;

  // Extract the first letter of the name
  const initial = name?.charAt(0).toUpperCase() || 'U';

  return (
    <div
      className={[
        'relative m-10 flex flex-shrink-0 flex-col justify-between overflow-hidden rounded-[28px] border-[1.5px] p-[clamp(24px,3.5vw,42px)] transition-all duration-500',
        isActive ? 'translate-y-[-4px] scale-[1.015]' : 'scale-[0.97]',
      ].join(' ')}
      style={{
        minWidth: 'clamp(270px, 42vw, 450px)',
        maxWidth: 'clamp(270px, 42vw, 450px)',
        background: isActive
          ? `linear-gradient(150deg, ${light} 0%, white 100%)`
          : `linear-gradient(150deg, ${light}88 0%, #f8fafc 100%)`,
        borderColor: isActive ? `${accent}50` : '#e2e8f0',
        boxShadow: isActive
          ? `0 24px 60px ${accent}1a, 0 4px 16px ${accent}10`
          : '0 2px 12px rgba(0,0,0,0.05)',
        opacity: isActive ? 1 : 0.68,
      }}
    >
      <div>
        {/* Rating Section */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                style={{color: i < rating ? accent : '#cbd5e1'}}
                className="text-[18px]"
              >
                ★
              </span>
            ))}
          </div>
          {/* Subtle Quote Mark */}
          <span
            className="absolute right-8 top-6 font-serif text-[60px] leading-none opacity-10"
            style={{color: accent}}
          >
            “
          </span>
        </div>

        <p className="font-archivo mb-8 text-[clamp(14px,1.15vw,16px)] font-medium leading-[1.7] text-[#334155]">
          "{review}"
        </p>
      </div>

      <div className="flex items-center gap-10">
        {/* AVATAR / INITIAL BOX */}
        <div
          className="font-archivo flex h-50 w-50 shrink-0 items-center justify-center rounded-full border-2 text-[20px] font-bold"
          style={{
            borderColor: `${accent}40`,
            background: isActive ? `${accent}15` : '#f1f5f9',
            color: accent,
          }}
        >
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <span>{initial}</span>
          )}
        </div>

        <div className="overflow-hidden ">
          <h4 className="font-archivo truncate text-[17px] font-bold leading-tight text-[#0f172a]">
            {name}
          </h4>
          <p
            className="truncate text-[13px] font-semibold"
            style={{color: accent}}
          >
            {role}
          </p>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 h-[3px] rounded-bl-[28px] transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          width: isActive ? '100%' : '0%',
          background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
        }}
      />
    </div>
  );
}
